// 1. React and core hooks
import { useCallback, useMemo } from 'react';

// 2. External libraries
import { getRouteApi } from '@tanstack/react-router';

// 3. Internal components
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { LoadingSpinner } from '~/components/common/LoadingSpinner';
import { SinglePurposePageLayout } from '~/components/common';
import { OAuthConsentCard } from '~/components/oauth';
import { Button } from '~/components/ui/button';

// 4. Type imports

// 5. Custom hooks and utilities
import { useTopicsQuery } from '~/hooks/useTopicsQuery';
import { useOAuthAuthorize } from '~/hooks/useOAuthAuthorize';
import { useOAuthClientMetadata } from '~/hooks/useOAuthClientMetadata';
import { useAuthorizedAppsQuery } from '~/hooks/useOAuthQueries';

/**
 * OAuth Authorization Page
 *
 * Route: /oauth/authorize (requires authentication)
 *
 * Displays OAuth consent screen where users approve third-party application access.
 * Parses OAuth parameters from URL and presents them for user authorization.
 *
 * Per spec lines 257-279:
 * - Reads OAuth parameters from URL query string
 * - Validates redirect_uri is present
 * - Fetches user topics for connection selection
 * - Shows loading state during data fetch
 * - Shows error state for missing/invalid parameters
 */
const routeApi = getRouteApi('/oauth/authorize');

export function OAuthAuthorizePage() {
  const search = routeApi.useSearch();

  // Parse OAuth parameters from URL (per spec lines 260-261)
  // Normalize undefined to null so downstream consumers keep their `string | null` contract.
  const clientId = search.client_id ?? null;
  const redirectUri = search.redirect_uri ?? null;
  const _responseType = search.response_type ?? null; // Reserved for future validation
  const scope = search.scope ?? null;
  const state = search.state ?? null;
  const codeChallenge = search.code_challenge ?? null;
  const codeChallengeMethod = search.code_challenge_method ?? null;

  // Fetch user topics (per spec lines 262)
  // useTopicsQuery pages through and returns every topic the user owns.
  const { data: topicsResponse, isLoading, error, refetch: refetchTopics } = useTopicsQuery();

  // Fetch OAuth client metadata
  const {
    data: metadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
    refetch: refetchMetadata,
  } = useOAuthClientMetadata(clientId);

  // Fetch authorized apps to check for existing client
  const { data: authorizedApps, isLoading: isCheckingExisting } = useAuthorizedAppsQuery();

  // Derive client_type from metadata (normalized to lowercase)
  // Per spec: 'chatgpt' or 'claude'
  const clientType = metadata?.clientName?.toLowerCase() as 'chatgpt' | 'claude' | undefined;

  // Find existing client by matching clientType (per spec Section 2.2)
  const existingClient = clientType
    ? (authorizedApps?.find((c) => c.clientType === clientType) ?? null)
    : null;

  // OAuth authorization hook
  const {
    authorize,
    isLoading: isAuthorizing,
    error: authorizeError,
    reset: resetAuthorize,
  } = useOAuthAuthorize();

  // Display name for the requesting app, used in "Return to {app}" dead-end actions.
  const appDisplayName = metadata?.clientName ?? clientId ?? 'the application';

  // Suppress unused variable warning - reserved for future use
  void _responseType;

  // Validate redirect_uri is present (per spec lines 264)
  const hasRedirectUri = Boolean(redirectUri);

  // Parse scopes from space-separated string (OAuth standard)
  // Memoized to prevent useCallback dependencies from changing on every render (per §TS.13.1)
  const requestedScopes = useMemo(
    () => (scope ? scope.split(' ').filter((s) => s.trim()) : []),
    [scope]
  );

  // Handle authorization callback (per §TS.13.1)
  // Must be defined before early returns per React hooks rules
  const handleAuthorize = useCallback(
    async (topicId: string) => {
      if (!clientId || !redirectUri) return;

      // Use the scopes requested by the OAuth client (from URL query params)
      // Don't modify - grant exactly what was requested
      await authorize({
        clientId,
        redirectUri,
        topicId,
        scopes: requestedScopes,
        state: state || undefined,
        codeChallenge: codeChallenge || undefined,
        codeChallengeMethod: (codeChallengeMethod as 'S256') || undefined,
      });
    },
    [clientId, redirectUri, state, codeChallenge, codeChallengeMethod, authorize, requestedScopes]
  );

  // Handle cancel callback (per §TS.13.1)
  // Must be defined before early returns per React hooks rules
  const handleCancel = useCallback(() => {
    if (!redirectUri) return;

    // Redirect to client with error=access_denied
    const url = new URL(redirectUri);
    url.searchParams.set('error', 'access_denied');
    if (state) {
      url.searchParams.set('state', state);
    }
    window.location.href = url.toString();
  }, [redirectUri, state]);

  // "Return to {app}" dead-end action: redirects back with error=access_denied
  // so the requesting app can recover instead of leaving the user stranded.
  const handleReturnToApp = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  // Display loading state while fetching topics (per spec lines 263)
  if (isLoading) {
    return (
      <SinglePurposePageLayout showLogo={false}>
        <LoadingSpinner message="Loading authorization request..." />
      </SinglePurposePageLayout>
    );
  }

  // Display error state for missing/invalid redirect_uri (per spec lines 265)
  if (!hasRedirectUri) {
    return (
      <SinglePurposePageLayout>
        <ErrorAlert
          title="Invalid Authorization Request"
          message="Missing required parameter: redirect_uri"
          details="The authorization request is missing the redirect_uri parameter. Please contact the application developer."
        />
      </SinglePurposePageLayout>
    );
  }

  // Display error state for topics fetch failure
  if (error) {
    return (
      <SinglePurposePageLayout>
        <ErrorAlert
          title="Failed to Load Topics"
          message="Unable to load your topics. Please try again."
          details={error.message}
          onRetry={() => void refetchTopics()}
        />
        <Button variant="link" className="mt-4" onClick={handleReturnToApp}>
          Return to {appDisplayName}
        </Button>
      </SinglePurposePageLayout>
    );
  }

  // Display authorization error if present
  if (authorizeError) {
    return (
      <SinglePurposePageLayout>
        <ErrorAlert
          title="Authorization Failed"
          message="Unable to complete authorization. Please try again."
          details={authorizeError.message}
          onRetry={resetAuthorize}
        />
        <Button variant="link" className="mt-4" onClick={handleReturnToApp}>
          Return to {appDisplayName}
        </Button>
      </SinglePurposePageLayout>
    );
  }

  // Display error if metadata fetch failed (network error)
  if (metadataError) {
    return (
      <SinglePurposePageLayout>
        <ErrorAlert
          title="Failed to Load Application"
          message="Unable to load application information. Please try again."
          details={metadataError.message}
          onRetry={() => void refetchMetadata()}
        />
        <Button variant="link" className="mt-4" onClick={handleReturnToApp}>
          Return to {appDisplayName}
        </Button>
      </SinglePurposePageLayout>
    );
  }

  // Display error if OAuth client is not registered (404 from backend)
  // This occurs when the client_id doesn't exist in the database
  if (metadata && !metadata.isRegistered) {
    return (
      <SinglePurposePageLayout>
        <ErrorAlert
          title="Application Not Found"
          message="This application is not registered with DeadDrop. The authorization request may have expired or the application was removed."
          details={`Client ID: ${clientId}`}
        />
        <Button variant="link" className="mt-4" onClick={handleReturnToApp}>
          Return to {appDisplayName}
        </Button>
      </SinglePurposePageLayout>
    );
  }

  // Render OAuthConsentCard component
  // After hasRedirectUri check, redirectUri is guaranteed to be non-null
  return (
    <SinglePurposePageLayout>
      <OAuthConsentCard
        clientId={clientId}
        redirectUri={redirectUri!}
        requestedScopes={requestedScopes}
        state={state}
        topics={topicsResponse?.topics || []}
        metadata={metadata ?? null}
        isLoadingMetadata={isLoadingMetadata}
        existingClient={existingClient}
        isCheckingExisting={isCheckingExisting}
        onAuthorize={(topicId) => void handleAuthorize(topicId)}
        onCancel={handleCancel}
        isAuthorizing={isAuthorizing}
      />
    </SinglePurposePageLayout>
  );
}
